import React, { useState, Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
// 移除直接导入，改为懒加载
// import LandingPage from './components/Questionnaire/WhyUs/LandingPage/LandingPage';
// import PrivacyPolicy from './components/Questionnaire/WhyUs/PrivacyPolicy/PrivacyPolicy';
// import TermsConditions from './components/Questionnaire/WhyUs/TermsConditions/TermsConditions';
// import Questionnaire from './components/Questionnaire/Questionnaire';
// import LoginPage from './components/Questionnaire/SignUpPage/LoginPage';
// import VerifyEmailPage from './components/Questionnaire/SignUpPage/VerifyEmailPage';
// import SignUpPage from './components/Questionnaire/SignUpPage/SignUpPage';
// import HomePage from './components/Discover/HomePage';
// import VibeSelection from './components/Stylist/VibeSelection/VibeSelection';
// import ChatPage from './components/Stylist/ChatPage/ChatPage';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './components/PersonalReport/Utils/AuthContext';
import { ReportProvider } from './components/ReportContext';
import { CreditsProvider } from './components/PersonalReport/Utils/CreditsProvider';
import { PrimaryGenderProvider } from './components/PersonalReport/Utils/PrimaryGenderContext';
import { PaymentIntentProvider, usePaymentIntent } from './components/PersonalReport/Utils/PaymentIntentContext';
import PaymentIntentModal from './components/PersonalReport/PaymentIntentModal';
import MyStylistPage from './components/Discover/MyStylistPage/MyStylistPage';
import AlphaModal from './components/AlphaModal';
import { PostHogProvider } from 'posthog-js/react';
import { trackEvent } from './utils/analytics';
// 移除 TensorFlow.js 的直接导入，改为懒加载
// import * as tf from "@tensorflow/tfjs";  // 这是主模块
// import "@tensorflow/tfjs-backend-webgl";  // WebGL 后端
// import "@tensorflow/tfjs-backend-wasm";

// 懒加载页面组件
const LandingPage = lazy(() => import('./components/Questionnaire/WhyUs/LandingPage/LandingPage'));
const OptimizedLandingPage = lazy(() => import('./components/Questionnaire/WhyUs/OptimizedLandingPage/OptimizedLandingPage'));
const PrivacyPolicy = lazy(() => import('./components/Questionnaire/WhyUs/PrivacyPolicy/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./components/Questionnaire/WhyUs/TermsConditions/TermsConditions'));
const Questionnaire = lazy(() => import('./components/Questionnaire/Questionnaire'));
const LoginPage = lazy(() => import('./components/Questionnaire/SignUpPage/LoginPage'));
const VerifyEmailPage = lazy(() => import('./components/Questionnaire/SignUpPage/VerifyEmailPage'));
const SignUpPage = lazy(() => import('./components/Questionnaire/SignUpPage/SignUpPage'));
const ResetRequestPage = lazy(() => import('./components/Questionnaire/SignUpPage/ResetRequestPage'));
const ResetPasswordPage = lazy(() => import('./components/Questionnaire/SignUpPage/ResetPasswordPage'));
const HomePage = lazy(() => import('./components/Discover/HomePage'));
const VibeSelection = lazy(() => import('./components/Stylist/VibeSelection/VibeSelection'));
const ChatPage = lazy(() => import('./components/Stylist/ChatPage/ChatPage'));
const SharedStylePage = lazy(() => import('./components/PersonalReport/SharedStylePage'));

const options = {
  api_host: process.env.REACT_APP_POSTHOG_HOST,
};

// New AppRoutes component to use useLocation inside Router
const AppRoutes: React.FC = () => {
  // 用户留存与活跃埋点
  useEffect(() => {
    const now = Date.now();
    const today = new Date().toDateString();
    const lastVisit = localStorage.getItem('lastVisitDate');
    const firstVisit = localStorage.getItem('firstVisitDate');
    let consecutiveDays = Number(localStorage.getItem('consecutiveDays') || 1);

    // 首次访问
    if (!firstVisit) {
      localStorage.setItem('firstVisitDate', today);
      trackEvent('user_first_visit', { date: today });
    }

    // 每日首次访问
    if (lastVisit !== today) {
      if (lastVisit) {
        // 判断是否连续访问
        const last = new Date(lastVisit);
        const diff = (new Date(today).getTime() - last.getTime()) / (1000 * 3600 * 24);
        if (diff === 1) {
          consecutiveDays += 1;
        } else {
          consecutiveDays = 1;
        }
      }
      localStorage.setItem('lastVisitDate', today);
      localStorage.setItem('consecutiveDays', String(consecutiveDays));
      trackEvent('user_daily_visit', { date: today, consecutive_days: consecutiveDays });
    }
  }, []);

  const location = useLocation();
  const hideAlphaModal = location.pathname === '/' || location.pathname === '/landing';

  const PaymentIntentLayer: React.FC = () => {
    const {
      isPaymentIntentModalOpen,
      resolvePaymentIntentModal,
      lastModalTriggerSource,
      lastModalInteractionCount,
      lastModalOpenedAt,
    } = usePaymentIntent();
    const [hasTrackedCurrentModal, setHasTrackedCurrentModal] = useState(false);

    // 记录弹窗曝光
    useEffect(() => {
      if (isPaymentIntentModalOpen && !hasTrackedCurrentModal && lastModalTriggerSource) {
        const deviceType = window.innerWidth <= 900 ? 'mobile' : 'desktop';
        trackEvent('payment_intent_modal_shown', {
          trigger_source: lastModalTriggerSource,
          interaction_count_at_show: lastModalInteractionCount ?? 0,
          page: location.pathname,
          device_type: deviceType,
        });
        setHasTrackedCurrentModal(true);
      }

      if (!isPaymentIntentModalOpen) {
        setHasTrackedCurrentModal(false);
      }
    }, [
      isPaymentIntentModalOpen,
      hasTrackedCurrentModal,
      lastModalTriggerSource,
      lastModalInteractionCount,
      location.pathname,
    ]);

    if (!isPaymentIntentModalOpen) return null;

    const handleChoose = (choice: 'accept' | 'decline') => {
      const deviceType = window.innerWidth <= 900 ? 'mobile' : 'desktop';
      const decisionLatencyMs =
        typeof lastModalOpenedAt === 'number' ? Date.now() - lastModalOpenedAt : undefined;

      trackEvent('payment_intent_choice_made', {
        choice,
        trigger_source: lastModalTriggerSource ?? 'unknown',
        interaction_count_at_choice: lastModalInteractionCount ?? 0,
        decision_latency_ms: decisionLatencyMs,
        page: location.pathname,
        device_type: deviceType,
      });

      // 关闭弹窗
      resolvePaymentIntentModal(choice);
      // 这里暂时不直接跳转支付，只做调研
    };

    return <PaymentIntentModal onChoose={handleChoose} />;
  };

  return (
    <PostHogProvider apiKey={process.env.REACT_APP_POSTHOG_KEY || ''} options={options}>
      <AuthProvider>
        <PrimaryGenderProvider>
          <CreditsProvider>
            <PaymentIntentProvider>
              {!hideAlphaModal && <AlphaModal />}
              <PaymentIntentLayer />
              <Routes>
            {/* Public Routes */}
            <Route path="/" element={
              <Suspense fallback={<div>Loading...</div>}>
                <LandingPage />
              </Suspense>
            } />
            <Route path="/landing" element={
              <Suspense fallback={<div>Loading...</div>}>
                <OptimizedLandingPage />
              </Suspense>
            } />
            <Route path="/questionnaire" element={
              <Suspense fallback={<div>Loading...</div>}>
                <Questionnaire />
              </Suspense>
            } />
            <Route path="/privacy" element={
              <Suspense fallback={<div>Loading...</div>}>
                <PrivacyPolicy />
              </Suspense>
            } />
            <Route path="/terms" element={
              <Suspense fallback={<div>Loading...</div>}>
                <TermsConditions />
              </Suspense>
            } />
            <Route path="/login" element={
              <Suspense fallback={<div>Loading...</div>}>
                <LoginPage />
              </Suspense>
            } />
            <Route path="/verify-email" element={
              <Suspense fallback={<div>Loading...</div>}>
                <VerifyEmailPage />
              </Suspense>
            } />
            <Route path="/signup" element={
              <Suspense fallback={<div>Loading...</div>}>
                <SignUpPage userResponses={{}}/>
              </Suspense>
            } />
            <Route path="/mystylist" element={<MyStylistPage />} />
            <Route path="/request-password-reset" element={
              <Suspense fallback={<div>Loading...</div>}>
                <ResetRequestPage />
              </Suspense>
            } />
            <Route path="/reset-password" element={
              <Suspense fallback={<div>Loading...</div>}>
                <ResetPasswordPage />
              </Suspense>
            } />
            <Route path="/shared-style" element={
              <Suspense fallback={<div>Loading...</div>}>
                <SharedStylePage />
              </Suspense>
            } />
            {/* Routes wrapped with ReportProvider and ProtectedRoute */}
            <Route
              element={
                <ProtectedRoute>
                  <ReportProvider>
                    <Suspense fallback={<div>Loading...</div>}>
                      <VibeSelection />
                    </Suspense>
                  </ReportProvider>
                </ProtectedRoute>
              }
              path="/vibe-selection"
            />
            <Route
              element={
                <ProtectedRoute>
                  <ReportProvider>
                    <Suspense fallback={<div>Loading...</div>}>
                      <ChatPage />
                    </Suspense>
                  </ReportProvider>
                </ProtectedRoute>
              }
              path="/chatpage"
            />
            <Route
              element={
                <ProtectedRoute>
                  <ReportProvider>
                    <Suspense fallback={<div>Loading...</div>}>
                      <HomePage />
                    </Suspense>
                  </ReportProvider>
                </ProtectedRoute>
              }
              path="/home"
            />
          </Routes>
          </PaymentIntentProvider>
        </CreditsProvider>
        </PrimaryGenderProvider>
      </AuthProvider>
    </PostHogProvider>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
};

export default App;
